/**
 * UserMarketPerformanceView
 * User-facing market insights calendar with scope toggles (view, filter, level).
 * Shows daily market conditions with the user's performance overlay.
 */

import { ToggleGroup } from '@components/ui/AnalyticsFilterBar';
import type { TimePeriod, UserDayDetail } from '@hooks/useUserMarketStats';
import { useUserMarketStats } from '@hooks/useUserMarketStats';
import type { AnalysisLevel, AnalyticsView } from '@parlay-picker/shared/analyticsScopes';
import {
  ANALYSIS_LEVELS,
  ANALYTICS_FILTERS,
  ANALYTICS_VIEWS,
} from '@parlay-picker/shared/analyticsScopes';
import { formatShortDay } from '@parlay-picker/shared/formatting';
import { marketConditionLabel } from '@parlay-picker/shared/marketCondition';
import { hitRatePct } from '@parlay-picker/shared/parlayStats';
import { deviceDay } from '@parlay-picker/shared/time';
import { LeagueConditionsList } from '@views/lab/components/LeagueConditionsList';
import { MarketCalendarLegend } from '@views/lab/components/MarketCalendarLegend';
import {
  getCallColor,
  getConditionBg,
  getConditionColor,
  getHitRateColor,
} from '@views/lab/components/marketInsightsHelpers';
import {
  AIInsightsSection,
  PlayerPerformanceSection,
  PropTypeBreakdownSection,
  SituationalFactorsSection,
} from '@views/lab/components/marketInsightsSections';
import type { DailyMarketInsight } from '@views/lab/hooks/useLabDashboard';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { type ReactElement, useMemo, useState } from 'react';

// =============================================================================
// Summary Bar
// =============================================================================

function SummaryBar({
  data,
  level,
}: {
  data: DailyMarketInsight[];
  level: AnalysisLevel;
}): ReactElement {
  if (data.length === 0) {
    return <div />;
  }

  if (level === 'legs') {
    const totalLegs = data.reduce((s, d) => s + d.legStats.total, 0);
    const totalWins = data.reduce((s, d) => s + d.legStats.wins, 0);
    const avgHitRate = totalLegs > 0 ? (totalWins / totalLegs) * 100 : 0;
    const avgOverHit = data.reduce((s, d) => s + d.legStats.overHitRate, 0) / data.length;

    return (
      <div className="grid grid-cols-4 gap-3 text-center">
        <div className="bg-theme-surface/50 rounded-lg p-2">
          <div className="text-lg font-bold text-gray-200">{totalLegs}</div>
          <div className="text-[10px] text-theme-text-tertiary">Total Legs</div>
        </div>
        <div className="bg-theme-surface/50 rounded-lg p-2">
          <div className={`text-lg font-bold ${getHitRateColor(avgHitRate)}`}>
            {avgHitRate.toFixed(1)}%
          </div>
          <div className="text-[10px] text-theme-text-tertiary">Hit Rate</div>
        </div>
        <div className="bg-theme-surface/50 rounded-lg p-2">
          <div className={`text-lg font-bold ${getHitRateColor(avgOverHit)}`}>
            {avgOverHit.toFixed(1)}%
          </div>
          <div className="text-[10px] text-theme-text-tertiary">Over Hit</div>
        </div>
        <div className="bg-theme-surface/50 rounded-lg p-2">
          <div className="text-lg font-bold text-gray-200">{data.length}</div>
          <div className="text-[10px] text-theme-text-tertiary">Days</div>
        </div>
      </div>
    );
  }

  // Parlay level
  const totalParlays = data.reduce((s, d) => s + d.parlayStats.total, 0);
  const totalWins = data.reduce((s, d) => s + d.parlayStats.wins, 0);
  const totalLosses = data.reduce((s, d) => s + d.parlayStats.losses, 0);
  // Decided = won + lost only; the raw parlay total folds in push/void.
  const winRate = hitRatePct(totalWins, totalLosses) ?? 0;
  const totalUnits = data.reduce((s, d) => s + d.parlayStats.units, 0);

  return (
    <div className="grid grid-cols-4 gap-3 text-center">
      <div className="bg-theme-surface/50 rounded-lg p-2">
        <div className="text-lg font-bold text-gray-200">{totalParlays}</div>
        <div className="text-[10px] text-theme-text-tertiary">Parlays</div>
      </div>
      <div className="bg-theme-surface/50 rounded-lg p-2">
        <div className={`text-lg font-bold ${getHitRateColor(winRate)}`}>{winRate.toFixed(1)}%</div>
        <div className="text-[10px] text-theme-text-tertiary">Win Rate</div>
      </div>
      <div className="bg-theme-surface/50 rounded-lg p-2">
        <div
          className={`text-lg font-bold ${totalUnits >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
        >
          {totalUnits >= 0 ? '+' : ''}
          {totalUnits.toFixed(1)}u
        </div>
        <div className="text-[10px] text-theme-text-tertiary">Units</div>
      </div>
      <div className="bg-theme-surface/50 rounded-lg p-2">
        <div className="text-lg font-bold text-gray-200">{data.length}</div>
        <div className="text-[10px] text-theme-text-tertiary">Days</div>
      </div>
    </div>
  );
}

// =============================================================================
// Calendar Components
// =============================================================================

interface CalendarDayProps {
  date: Date;
  insight: DailyMarketInsight | null;
  isCurrentMonth: boolean;
  isSelected: boolean;
  level: AnalysisLevel;
  onClick: () => void;
}

function CalendarDay({
  date,
  insight,
  isCurrentMonth,
  isSelected,
  level,
  onClick,
}: CalendarDayProps): ReactElement {
  const dayNum = date.getDate();
  const hasData = insight !== null;

  const primaryStat = hasData
    ? level === 'legs'
      ? `${insight.legStats.hitRate}%`
      : `${insight.parlayStats.hitRate}%`
    : null;

  const secondaryStat = hasData
    ? level === 'legs'
      ? null // no secondary for legs — keep it clean
      : `${insight.parlayStats.units >= 0 ? '+' : ''}${insight.parlayStats.units.toFixed(1)}u`
    : null;

  const primaryColor = hasData
    ? level === 'legs'
      ? getCallColor(insight.overall.call)
      : insight.parlayStats.units >= 0
        ? 'text-theme-success'
        : 'text-theme-danger'
    : '';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!hasData}
      className={`
        relative p-1 min-h-[60px] rounded-lg text-xs transition-all border
        ${!isCurrentMonth ? 'opacity-30' : ''}
        ${hasData ? 'cursor-pointer hover:bg-white/5' : 'cursor-default'}
        ${isSelected ? 'ring-2 ring-blue-500' : ''}
        ${hasData ? getConditionBg(insight.marketCondition) : 'bg-theme-surface/20'}
        ${hasData ? 'border-theme-border/50' : 'border-transparent'}
      `}
    >
      <div className="absolute top-1 left-1.5 text-[10px] text-theme-text-secondary">{dayNum}</div>
      {hasData && (
        <div className="pt-4 flex flex-col items-center gap-0.5">
          <span className="text-lg">{insight.marketEmoji}</span>
          <span className={`font-semibold ${primaryColor}`}>{primaryStat}</span>
          {secondaryStat && (
            <span
              className={`text-[10px] ${insight.parlayStats.units >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
            >
              {secondaryStat}
            </span>
          )}
        </div>
      )}
    </button>
  );
}

function CalendarView({
  data,
  selectedDate,
  onSelectDate,
  level,
}: {
  data: DailyMarketInsight[];
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  level: AnalysisLevel;
}): ReactElement {
  const [viewDate, setViewDate] = useState(() => {
    if (data.length > 0) {
      return new Date(`${data[0].date}T12:00:00`);
    }
    return new Date();
  });

  const insightsByDate = useMemo(() => {
    const map = new Map<string, DailyMarketInsight>();
    for (const insight of data) {
      map.set(insight.date, insight);
    }
    return map;
  }, [data]);

  const calendarDays = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));
    const days: Date[] = [];
    const current = new Date(startDate);
    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  }, [viewDate]);

  const monthYear = viewDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
  const currentMonth = viewDate.getMonth();

  return (
    <div className="bg-theme-surface/30 rounded-lg p-4">
      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}
          className="p-2 hover:bg-theme-surface-hover/50 rounded transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-theme-text-secondary" />
        </button>
        <h3 className="text-lg font-semibold text-white">{monthYear}</h3>
        <button
          type="button"
          onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}
          className="p-2 hover:bg-theme-surface-hover/50 rounded transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-theme-text-secondary" />
        </button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div
            key={day}
            className="text-center text-[10px] text-theme-text-tertiary uppercase py-1"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((date) => {
          const dateStr = deviceDay(date);
          return (
            <CalendarDay
              key={dateStr}
              date={date}
              insight={insightsByDate.get(dateStr) || null}
              isCurrentMonth={date.getMonth() === currentMonth}
              isSelected={dateStr === selectedDate}
              level={level}
              onClick={() => onSelectDate(dateStr)}
            />
          );
        })}
      </div>

      <MarketCalendarLegend level={level} />
    </div>
  );
}

// =============================================================================
// Day Detail Modal
// =============================================================================

function DayDetailModal({
  insight,
  dayDetail,
  dayDetailLoading,
  level,
  view,
  onClose,
}: {
  insight: DailyMarketInsight;
  dayDetail: UserDayDetail | null;
  dayDetailLoading: boolean;
  level: AnalysisLevel;
  view: AnalyticsView;
  onClose: () => void;
}): ReactElement {
  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-theme-surface rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`p-4 border-b border-theme-border ${getConditionBg(insight.marketCondition)}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{insight.marketEmoji}</span>
              <div>
                <h2 className="text-lg font-semibold text-white">{formatShortDay(insight.date)}</h2>
                <p className={`text-sm ${getConditionColor(insight.marketCondition)}`}>
                  {marketConditionLabel(insight.marketCondition)}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-theme-surface-hover/50 rounded transition-colors"
            >
              <X className="w-5 h-5 text-theme-text-secondary" />
            </button>
          </div>
          <p className="text-sm text-theme-text-secondary italic mt-2">"{insight.insight}"</p>
        </div>

        {/* Stats */}
        <div className="p-4 space-y-4">
          <LeagueConditionsList leagues={insight.leagueConditions} />
          {/* Summary Row */}
          {level === 'legs' ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="bg-theme-surface/50 rounded-lg p-3">
                <div className={`text-2xl font-bold ${getCallColor(insight.overall.call)}`}>
                  {insight.legStats.hitRate}%
                </div>
                <div className="text-xs text-theme-text-tertiary mt-1">Hit Rate</div>
              </div>
              <div className="bg-theme-surface/50 rounded-lg p-3">
                <div className={`text-2xl font-bold ${getCallColor(insight.over.call)}`}>
                  {insight.legStats.overHitRate}%
                </div>
                <div className="text-xs text-theme-text-tertiary mt-1">Over Hit</div>
              </div>
              <div className="bg-theme-surface/50 rounded-lg p-3">
                <div className={`text-2xl font-bold ${getCallColor(insight.under.call)}`}>
                  {insight.legStats.underHitRate}%
                </div>
                <div className="text-xs text-theme-text-tertiary mt-1">Under Hit</div>
              </div>
              <div className="bg-theme-surface/50 rounded-lg p-3">
                <div className="text-2xl font-bold text-gray-200">{insight.legStats.total}</div>
                <div className="text-xs text-theme-text-tertiary mt-1">Total Legs</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="bg-theme-surface/50 rounded-lg p-3">
                <div
                  className={`text-2xl font-bold ${insight.parlayStats.units >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
                >
                  {insight.parlayStats.hitRate}%
                </div>
                <div className="text-xs text-theme-text-tertiary mt-1">Win Rate</div>
              </div>
              <div className="bg-theme-surface/50 rounded-lg p-3">
                <div
                  className={`text-2xl font-bold ${insight.parlayStats.units >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
                >
                  {insight.parlayStats.units >= 0 ? '+' : ''}
                  {insight.parlayStats.units.toFixed(2)}u
                </div>
                <div className="text-xs text-theme-text-tertiary mt-1">Profit/Loss</div>
              </div>
              <div className="bg-theme-surface/50 rounded-lg p-3">
                <div className="text-2xl font-bold text-gray-200">
                  {insight.parlayStats.wins}W - {insight.parlayStats.losses}L
                </div>
                <div className="text-xs text-theme-text-tertiary mt-1">Record</div>
              </div>
              <div className="bg-theme-surface/50 rounded-lg p-3">
                <div className="text-2xl font-bold text-gray-200">{insight.parlayStats.total}</div>
                <div className="text-xs text-theme-text-tertiary mt-1">Total Parlays</div>
              </div>
            </div>
          )}

          {/* Leg-Level Performance — only when legs selected */}
          {level === 'legs' && (
            <div className="bg-theme-surface/30 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-theme-text-secondary uppercase mb-3">
                Leg-Level Performance
              </h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-theme-text-tertiary text-xs">Record</div>
                  <div className="text-gray-200">
                    {insight.legStats.wins}W - {insight.legStats.total - insight.legStats.wins}L
                  </div>
                </div>
                <div>
                  <div className="text-theme-text-tertiary text-xs">Over Hit</div>
                  <div className={getCallColor(insight.over.call)}>
                    {insight.legStats.overHitRate}% ({insight.legStats.overCount})
                  </div>
                </div>
                <div>
                  <div className="text-theme-text-tertiary text-xs">Under Hit</div>
                  <div className={getCallColor(insight.under.call)}>
                    {insight.legStats.underHitRate}% ({insight.legStats.underCount})
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Parlay-Level Performance — only when parlays selected */}
          {level === 'parlays' && (
            <div className="bg-theme-surface/30 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-theme-text-secondary uppercase mb-3">
                Parlay-Level Performance
              </h4>
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-theme-text-tertiary text-xs">Record</div>
                  <div className="text-gray-200">
                    {insight.parlayStats.wins}W - {insight.parlayStats.losses}L
                  </div>
                </div>
                <div>
                  <div className="text-theme-text-tertiary text-xs">All-Over</div>
                  <div className={getHitRateColor(insight.parlayStats.allOverHitRate)}>
                    {insight.parlayStats.allOverHitRate}% ({insight.parlayStats.allOverParlays})
                  </div>
                </div>
                <div>
                  <div className="text-theme-text-tertiary text-xs">All-Under</div>
                  <div className={getHitRateColor(insight.parlayStats.allUnderHitRate)}>
                    {insight.parlayStats.allUnderHitRate}% ({insight.parlayStats.allUnderParlays})
                  </div>
                </div>
                <div>
                  <div className="text-theme-text-tertiary text-xs">Mixed</div>
                  <div className={getHitRateColor(insight.parlayStats.mixedHitRate)}>
                    {insight.parlayStats.mixedHitRate}% ({insight.parlayStats.mixedParlays})
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Prop Type Breakdown */}
          <PropTypeBreakdownSection stats={insight.propTypeStats} />

          {/* Situational Factors */}
          {insight.situationalStats && (
            <SituationalFactorsSection stats={insight.situationalStats} />
          )}

          {/* Player Performance */}
          {insight.playerStats &&
            (insight.playerStats.hotPlayers.length > 0 ||
              insight.playerStats.coldPlayers.length > 0) && (
              <PlayerPerformanceSection stats={insight.playerStats} />
            )}

          {/* AI Insights — only in global (ai_community) scope */}
          {view === 'ai_community' && insight.ai && <AIInsightsSection ai={insight.ai} />}

          {/* User's Tracked Parlays for this Day */}
          {dayDetailLoading && (
            <div className="bg-theme-surface/30 rounded-lg p-4 text-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-theme-primary mx-auto mb-2" />
              <p className="text-theme-text-secondary text-xs">Loading your bets...</p>
            </div>
          )}
          {dayDetail && dayDetail.parlays.length > 0 && (
            <div className="bg-theme-surface/30 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-theme-text-secondary uppercase mb-3">
                Your Bets ({dayDetail.summary.totalParlays})
                <span className="ml-2 font-normal text-theme-text-tertiary">
                  {dayDetail.summary.wins}W - {dayDetail.summary.losses}L
                  {dayDetail.summary.pending > 0 && ` - ${dayDetail.summary.pending} pending`}
                </span>
              </h4>
              <div className="space-y-2">
                {dayDetail.parlays.map((parlay) => (
                  <div key={parlay.id} className="bg-theme-surface/50 rounded-lg p-2.5">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs text-theme-text-secondary font-medium">
                        {parlay.name || `Parlay #${parlay.id}`}
                      </span>
                      <span
                        className={`text-xs font-semibold ${
                          parlay.result === 'won'
                            ? 'text-theme-success'
                            : parlay.result === 'lost'
                              ? 'text-theme-danger'
                              : 'text-theme-text-tertiary'
                        }`}
                      >
                        {parlay.result?.toUpperCase() || 'PENDING'}
                        {parlay.totalOdds && (
                          <span className="text-theme-text-tertiary ml-1">
                            ({parlay.totalOdds})
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {parlay.legs.map((leg, i) => (
                        <div key={i} className="flex items-center justify-between text-[11px]">
                          <span className="text-theme-text-secondary">
                            {leg.player} — {leg.prop}
                          </span>
                          <span
                            className={
                              leg.hit === true
                                ? 'text-theme-success'
                                : leg.hit === false
                                  ? 'text-theme-danger'
                                  : 'text-theme-text-tertiary'
                            }
                          >
                            {leg.hit === true ? 'HIT' : leg.hit === false ? 'MISS' : '—'}
                            {leg.actualValue && (
                              <span className="text-gray-600 ml-1">({leg.actualValue})</span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {dayDetail && dayDetail.parlays.length === 0 && (
            <div className="bg-theme-surface/30 rounded-lg p-4 text-center">
              <p className="text-theme-text-tertiary text-xs">No tracked bets on this day</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Main View
// =============================================================================

export function UserMarketPerformanceView(): ReactElement {
  const {
    data,
    loading,
    error,
    view,
    setView,
    filter,
    setFilter,
    validFilters,
    level,
    setLevel,
    period,
    setPeriod,
    dayDetail,
    dayDetailLoading,
    fetchDayDetail,
    clearDayDetail,
    nextRefreshAt,
    refresh,
  } = useUserMarketStats();

  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const selectedInsight = selectedDate ? data.find((d) => d.date === selectedDate) : null;

  const handleSelectDate = (date: string) => {
    setSelectedDate(date);
    // Fetch user's day detail if in user-scoped view
    if (view === 'my_stats') {
      fetchDayDetail(date);
    }
  };

  const handleCloseModal = () => {
    setSelectedDate(null);
    clearDayDetail();
  };

  // Format next refresh time for display
  const refreshLabel = nextRefreshAt
    ? new Date(nextRefreshAt).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      })
    : null;

  if (loading) {
    return (
      <div className="bg-theme-surface/50 rounded-lg p-8 text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-theme-primary mx-auto mb-2" />
        <p className="text-theme-text-secondary text-sm">Loading market insights...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-theme-surface/50 rounded-lg p-4">
        <p className="text-theme-danger text-sm">{error}</p>
        <button
          type="button"
          onClick={refresh}
          className="mt-2 px-3 py-1 bg-theme-surface-hover text-theme-text-secondary rounded text-sm hover:bg-theme-surface-hover"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header + Toggles */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Market Performance</h2>
            <p className="text-xs text-theme-text-tertiary mt-0.5">
              Daily market conditions and betting performance
              {refreshLabel && <span className="ml-1">· Updates at {refreshLabel}</span>}
            </p>
          </div>
        </div>

        {/* Toggle Row */}
        <div className="flex flex-wrap items-center gap-3">
          {/* View Toggle */}
          <ToggleGroup
            options={[
              {
                value: 'ai_community' as const,
                label: ANALYTICS_VIEWS.ai_community.label,
                description: ANALYTICS_VIEWS.ai_community.description,
              },
              {
                value: 'my_stats' as const,
                label: ANALYTICS_VIEWS.my_stats.label,
                description: ANALYTICS_VIEWS.my_stats.description,
              },
            ]}
            selected={view}
            onChange={setView}
          />

          {/* Filter Toggle */}
          <ToggleGroup
            options={validFilters.map((f) => ({
              value: f,
              label: ANALYTICS_FILTERS[f].label,
              description: ANALYTICS_FILTERS[f].description,
            }))}
            selected={filter}
            onChange={setFilter}
          />

          {/* Separator */}
          <div className="w-px h-5 bg-theme-surface-hover" />

          {/* Analysis Level Toggle */}
          <ToggleGroup
            options={[
              { value: 'legs' as const, label: ANALYSIS_LEVELS.legs.label },
              {
                value: 'parlays' as const,
                label: ANALYSIS_LEVELS.parlays.label,
              },
            ]}
            selected={level}
            onChange={setLevel}
          />

          {/* Separator */}
          <div className="w-px h-5 bg-theme-surface-hover" />

          {/* Time Period Toggle */}
          <ToggleGroup<TimePeriod>
            options={[
              { value: '7d', label: '7D' },
              { value: '14d', label: '14D' },
              { value: '30d', label: '30D' },
              { value: '60d', label: '60D' },
              { value: 'all', label: 'All' },
            ]}
            selected={period}
            onChange={setPeriod}
          />
        </div>
      </div>

      {/* Summary Bar */}
      <SummaryBar data={data} level={level} />

      {/* Calendar */}
      {data.length === 0 ? (
        <div className="bg-theme-surface/50 rounded-lg p-8 text-center">
          <p className="text-theme-text-secondary text-sm">
            {view === 'my_stats'
              ? 'No tracked bets found. Track some parlays to see your performance here.'
              : 'No market data available yet. Data will appear after settlement runs.'}
          </p>
        </div>
      ) : (
        <CalendarView
          data={data}
          selectedDate={selectedDate}
          onSelectDate={handleSelectDate}
          level={level}
        />
      )}

      {/* Day Detail Modal */}
      {selectedInsight && (
        <DayDetailModal
          insight={selectedInsight}
          dayDetail={dayDetail}
          dayDetailLoading={dayDetailLoading}
          level={level}
          view={view}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
